import {
	ClientListPage,
	ClientDetailPage,
	ScanListPage,
	ScanCreatePage,
	ScanDetailPage
} from '../components/pages';
import Dashboard from '../pages/Dashboard';
import AdminPage from '../pages/AdminPage';
import GlobalAdminPage from '../pages/GlobalAdminPage';
import FlaggedContentRedirect from '../pages/FlaggedContentRedirect';
import Login from '../pages/Login';
import NotFound from '../pages/NotFound';
import Register from '../pages/Register';
import Reports from '../pages/Reports';
import Support from '../pages/Support';
import RepGuardInstall from '../pages/RepGuardInstall';
import MonitoringPage from '../pages/MonitoringPage';
import HeartbeatPage from '../pages/HeartbeatPage';
import HeartbeatRunPage from '../pages/HeartbeatRunPage';
import HeartbeatIndexPage from '../pages/HeartbeatIndexPage';
import HeartbeatContextPage from '../pages/HeartbeatContextPage';
import SettingsPage from '../pages/SettingsPage';
import { Onboarding, LandingPage, ForgotPassword, ResetPassword, PrivacyPolicy, TermsOfService } from '../pages';
import { PATHS, ROUTE_PATTERNS, ROUTE_CATEGORIES } from '../constants/Paths';

/**
 * Centralized route configuration for the application
 * 
 * This file contains all route definitions organized by category:
 * - PUBLIC: Routes accessible without authentication
 * - PROTECTED: Routes requiring authentication + standard layout
 * - ADMIN: Routes requiring authentication + admin access
 * - REDIRECTS: Route redirects and catch-all routes
 */

// Public routes (no authentication required)
export const publicRoutes = [
	{
		path: PATHS.LANDING,
		element: LandingPage,
		requiresAuth: false,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
	{
		path: PATHS.LOGIN,
		element: Login,
		requiresAuth: false,
		redirectIfAuthenticated: PATHS.DASHBOARD,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
	{
		path: PATHS.REGISTER,
		element: Register,
		requiresAuth: false,
		redirectIfAuthenticated: PATHS.DASHBOARD,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
	{
		path: PATHS.PRIVACY,
		element: PrivacyPolicy,
		requiresAuth: false,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
	{
		path: PATHS.TERMS,
		element: TermsOfService,
		requiresAuth: false,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
	{
		path: "/forgot-password",
		element: ForgotPassword,
		requiresAuth: false,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
	{
		path: "/reset-password",
		element: ResetPassword,
		requiresAuth: false,
		category: ROUTE_CATEGORIES.PUBLIC,
	},
];

// Protected routes (authentication required, standard layout)
export const protectedRoutes = [
	{
		path: PATHS.HEARTBEAT_NOTES,
		element: HeartbeatContextPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	{
		path: PATHS.DASHBOARD,
		element: Dashboard,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: PATHS.CLIENT_LIST,
		element: ClientListPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: PATHS.SCAN_LIST,
		element: ScanListPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: PATHS.NEW_SCANS,
		element: ScanCreatePage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: PATHS.MONITORING,
		element: MonitoringPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	{
		// /heartbeat is the cross-account index. It used to render the account
		// page with no account, which could only say "Pick an account to listen
		// to" - the most useful screen in the feature was an empty prompt.
		path: PATHS.HEARTBEAT,
		element: HeartbeatIndexPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	{
		path: PATHS.REPORTS,
		element: Reports,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: PATHS.SUPPORT,
		element: Support,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: PATHS.REPGUARD_INSTALL,
		element: RepGuardInstall,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	{
		path: PATHS.SETTINGS,
		element: SettingsPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	{
		// ADMIN category, not PROTECTED: that is what applies GlobalAdminGuard
		// in Routes.jsx. Previously this was PROTECTED, so the staff console was
		// merely hidden from the nav and any signed-in customer could reach it
		// by typing the URL.
		path: PATHS.ADMIN,
		element: AdminPage,
		category: ROUTE_CATEGORIES.ADMIN,
		requiresAuth: true,
		requiresLayout: true,
	},
	{
		path: PATHS.GLOBAL_ADMIN,
		element: GlobalAdminPage,
		category: ROUTE_CATEGORIES.ADMIN,
		requiresAuth: true,
		requiresLayout: true,
	},
];

// Onboarding routes (authentication required, no layout)
export const onboardingRoutes = [
	{
		path: PATHS.ONBOARDING,
		element: Onboarding,
		category: ROUTE_CATEGORIES.ONBOARDING,
		requiresAuth: true,
		requiresLayout: false, // Full-screen experience
		// No onboarding guard - onboarding should always be accessible to authenticated users
	},
];

// Dynamic routes (routes with parameters)
export const dynamicRoutes = [
	{
		path: ROUTE_PATTERNS.CLIENT_DETAIL,
		element: ClientDetailPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	{
		path: ROUTE_PATTERNS.SCAN_DETAIL,
		element: ScanDetailPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true, // Add onboarding guard
	},
	// Listed before HEARTBEAT_ACCOUNT: react-router picks the more specific
	// pattern regardless of order, but keeping them adjacent and specific-first
	// makes the nesting obvious to the next reader.
	{
		path: ROUTE_PATTERNS.HEARTBEAT_RUN,
		element: HeartbeatRunPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	{
		path: ROUTE_PATTERNS.HEARTBEAT_ACCOUNT,
		element: HeartbeatPage,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: true,
		requiresOnboardingGuard: true,
	},
	// The standalone flagged-content page merged into the scan report's
	// Flagged tab; the old URL redirects so bookmarks and shared links keep
	// working.
	{
		path: ROUTE_PATTERNS.SCAN_FLAGGED_CONTENT,
		element: FlaggedContentRedirect,
		category: ROUTE_CATEGORIES.PROTECTED,
		requiresAuth: true,
		requiresLayout: false,
	},
];

// Redirect routes
export const redirectRoutes = [
	// A real 404, not a silent bounce to marketing - a signed-in user hitting a
	// stale bookmark used to land on the landing page, which reads as "logged
	// out" and is much more alarming than a plain not-found.
	{
		path: "*",
		element: NotFound,
		category: "redirect",
	},
];

// Combined route configuration
export const routeConfig = {
	public: publicRoutes,
	protected: protectedRoutes,
	onboarding: onboardingRoutes,
	dynamic: dynamicRoutes,
	redirects: redirectRoutes,
};

// All routes flattened for easy iteration
export const allRoutes = [
	...publicRoutes,
	...protectedRoutes,
	...onboardingRoutes,
	...dynamicRoutes,
	...redirectRoutes,
];

export default routeConfig;
